/**
 * ranking-regulamento.js — conteúdo da tela Rankings > "Regras e Regulamentos"
 * (router/views/rankings/regulamento.vue).
 *
 * Uma seção por ranking do Ranking Nacional (10) e do Ranking Estadual (8), na ordem do menu. Cada
 * seção é COMPLETA de propósito: o texto nunca diz "igual ao ranking X" — quem abre o regulamento
 * pelo botão de um ranking cai direto na seção dele e precisa ler tudo ali. Por isso o código
 * reaproveita os trechos (frases, passos do i4pro), mas cada seção sai com o texto inteiro.
 *
 * O `id` da seção é o id do ranking (o mesmo de `meta.rankingNacional` / `meta.rankingEstadual`),
 * que vira a âncora `#id` usada pelo botão "Ver regulamento" de nacional.vue.
 *
 * As regras foram validadas uma a uma com o negócio em 2026-10-13 e espelham o cálculo da API
 * (salesteams_app_api, models/rankings/Model_rankings_nacional_* e _estadual_*). Se uma regra
 * mudar lá, este texto precisa mudar junto. Não citar blacklist nem diretoria aqui.
 */
import { RANKINGS_NACIONAIS, ORDEM_DESEMPATE } from "@/utils/ranking-nacional";
import { RANKINGS_ESTADUAIS, RANKING_ESTADUAL_ATIVO } from "@/utils/ranking-estadual";

const IMG = {
  menuBrasil: require("@/assets/images/rankings-regulamento/i4pro/menu-relatorios.png"),
  formularioMetas: require("@/assets/images/rankings-regulamento/i4pro/formulario-metas.png"),
  metasAnoMes: require("@/assets/images/rankings-regulamento/i4pro/metas-ano-mes.png"),
  novosContratos: require("@/assets/images/rankings-regulamento/i4pro/novos-contratos-ano-mes.png"),
  recebimento: require("@/assets/images/rankings-regulamento/i4pro/recebimento-ano-mes-referencia.png"),
  baseHierarquia: require("@/assets/images/rankings-regulamento/i4pro/base-hierarquia-situacao.png"),
  menuTruck: require("@/assets/images/rankings-regulamento/i4pro/truck-menu-grupo-formulario.png"),
};

/** Vale para todos os rankings: aparece no topo da página e repetido em cada seção. */
export const AVISO_ATUALIZACAO =
  "Os relatórios do Teams Connect são atualizados todos os dias às 6h da manhã. No i4pro os relatórios são atualizados na hora, então um relatório baixado do i4pro depois desse horário pode mostrar diferença em relação ao ranking do dia.";

/**
 * Filtro de data que acompanha o passo a passo, para o relatório bater com a foto das 6h.
 *
 * Por quê (explicado pelo negócio em 2026-10-13): o contrato conta pela Data Início Vigência, mas
 * só aparece nos relatórios depois de ativado (análise de documentos/fotos), e a ativação é a
 * "Data Contrato" — que cai durante o expediente. O Teams Connect baixa o i4pro às 6h, antes de
 * qualquer ativação do dia; então um relatório baixado mais tarde traz contratos ativados HOJE que
 * o ranking ainda não tem. O mesmo vale para a "Data Pagamento" do Recebimento. O que importa é
 * tirar o dia de hoje — por isso o texto não fala em "dia 01" (no relatório de Metas a Data
 * Contrato pode ser do mês anterior).
 */
const MOTIVO_FILTRO_HOJE = {
  "Data Contrato":
    "O contrato só aparece no relatório depois de ativado (análise de documentos e fotos), e a Data Contrato é o dia dessa ativação. Como o ranking usa o relatório baixado às 6h, antes do expediente, os contratos ativados hoje só entram no ranking de amanhã.",
  "Data Pagamento":
    "Como o ranking usa o relatório baixado às 6h, os boletos pagos hoje só entram no ranking de amanhã.",
};

function passoFiltroAteOntem(coluna) {
  return {
    texto: `Filtrar a coluna "${coluna}" tirando o dia de hoje: deixe marcadas só as datas até ontem. Exemplo: hoje é 13/10/2026, então desmarque o dia 13/10/2026. ${MOTIVO_FILTRO_HOJE[coluna]}`,
  };
}

export const BLOCO_ESTADUAL =
  "Usa o mesmo cálculo do Ranking Nacional, mas mostra apenas os participantes do seu estado. As posições são contadas dentro do estado (1º, 2º, 3º…).";

const OBS_ADESAO_MES_SEGUINTE = {
  texto:
    "Depois que o mês fecha, pode aparecer alguma adesão do mês que só foi ativada no sistema no mês seguinte. Por isso, baixe também o relatório do mês seguinte, filtre a coluna \"Data Início Vigência\" pelo mês do ranking e some com o primeiro relatório.",
  exemplo:
    "Hoje é 05/10/2026 e estou apurando o ranking de 09/2026. Baixo os relatórios de 09 e de 10, junto os dois e filtro a Data Início Vigência do mês 09.",
};

// ── Frases ───────────────────────────────────────────────────────────────

function truck(ctx) {
  return ctx.truck ? " Truck" : "";
}

/** Texto de um critério de desempate, no vocabulário do ranking (regional/consultor, Brasil/Truck). */
function criterio(key, ctx) {
  const t = truck(ctx);
  const proprio = ctx.consultor;
  switch (key) {
    case "ticketMedio":
      return {
        nome: "Ticket Médio",
        texto: `soma dos valores recebidos ÷ quantidade de mensalidades${t} pagas no mês do ranking${proprio ? ", só do próprio consultor" : ""}.`,
      };
    case "adesoes":
      return {
        nome: "Adesões",
        texto: `quantidade de adesões${t}${proprio ? " do próprio consultor" : ""} com início de vigência no mês do ranking.`,
      };
    case "boletosPagos":
      return {
        nome: "Boletos Quitados",
        texto: `1º boletos das adesões${t}${proprio ? " do próprio consultor" : ""} feitas no mês anterior ao do ranking que foram pagos no prazo.`,
      };
    case "consultoresAtivos":
      return {
        nome: "Consultores Ativos",
        texto: `quantidade de consultores diferentes da regional que fizeram adesão${t} no mês. A própria regional não entra nessa conta.`,
      };
    case "inadimplencia":
      return {
        nome: "Inadimplência (%)",
        texto: `inadimplentes ÷ (ativos + inadimplentes) ${proprio ? "da própria base" : "da base"}${t} no mês do ranking. Quem tem menos vence.`,
      };
    default:
      return { nome: key, texto: "" };
  }
}

/** Mesma ordem da API: ORDEM_DESEMPATE sem a métrica do ranking (e sem Consultores Ativos p/ consultor). */
function desempate(metrica, ctx) {
  return ORDEM_DESEMPATE.filter((k) => k !== metrica && !(ctx.consultor && k === "consultoresAtivos")).map((k) =>
    criterio(k, ctx)
  );
}

function notaDesempate(ctx) {
  return ctx.consultor ? 'São só 3 critérios, porque "Consultores Ativos" não se aplica a um consultor.' : "";
}

function escopoProducao(ctx) {
  return ctx.consultor
    ? "Conta só a produção do próprio consultor, sem somar a equipe."
    : "Conta a produção de toda a regional: a dela e a dos consultores dela.";
}

const PUBLICO_TODAS = [
  "Todas as regionais ativas: Home Office, Executivo e acima.",
  "Não existe corte por tamanho de base.",
  "Ficam fora as regionais marcadas como Diretor.",
];

// Corte da base do consultor. Truck = 150 só no regulamento, a pedido do negócio (2026-10-21): a API
// (Model_rankings_*_truck_consultor*) ainda calcula com 300 até a regra ser alterada lá.
function corteBaseConsultor(ctx) {
  return ctx.truck ? 150 : 300;
}

function publicoConsultor(ctx) {
  const corte = corteBaseConsultor(ctx);
  return [
    "Consultores ativos. Quem também é regional fica fora deste ranking.",
    `Ficam fora os consultores que já chegaram a ${corte} ativos na própria base${truck(ctx)} em algum mês anterior ao do ranking. Ao chegar a ${corte} uma vez, o consultor sai deste ranking, mesmo que a base caia depois.`,
  ];
}

const PUBLICO = {
  homeoffice: () => [
    "Regionais Home Office ativas.",
    "Ficam fora as regionais que já tiveram 500 ou mais ativos na base em algum mês anterior ao mês do ranking. Ao chegar a 500 uma vez, a regional sai deste ranking, mesmo que a base caia depois.",
    'A base ativa considera os contratos com situação "Ativo" da regional e dos consultores dela.',
  ],
  todas: () => PUBLICO_TODAS,
  top50Ticket: () => [
    "Só participam as 50 primeiras regionais do ranking Top 20 Regional Adesão do mesmo mês.",
    "Ficam fora as regionais marcadas como Diretor.",
    "Por que o corte: sem ele, uma regional com pouquíssimas mensalidades poderia ter ticket médio alto por acaso e ficar no topo sem ter volume de operação.",
  ],
  top50Inadimplencia: () => [
    "Só participam as 50 regionais com mais adesões no mês do ranking.",
    "Ficam fora as regionais marcadas como Diretor.",
    "Por que o corte: sem ele, uma regional com a base quase zerada teria 0% de inadimplência \"de graça\" e ficaria no topo sem ter operação de verdade.",
  ],
  base100: () => [
    "Regionais ativas com mais de 100 ativos na base no mês anterior ao mês do ranking. Exemplo: no ranking de outubro/2026 vale a base ativa de setembro/2026.",
    "Com exatamente 100 ativos, a regional não participa.",
    "Ficam fora as regionais marcadas como Diretor.",
  ],
  consultor: publicoConsultor,
};

// ── O que conta ──────────────────────────────────────────────────────────

function contaBoletos(ctx) {
  const t = truck(ctx);
  return {
    titulo: `Boletos Quitados${t}`,
    itens: [`Os 1º boletos das adesões${t} feitas no mês anterior ao mês do ranking que foram pagos no prazo.`, escopoProducao(ctx)],
    exemplo: `Adesão${t} de setembro cujo 1º boleto (vencimento em outubro) foi pago no prazo, até 05 de novembro.`,
  };
}

function contaAdesoes(ctx) {
  const t = truck(ctx);
  return {
    titulo: `Adesões${t}`,
    itens: [
      `Quantidade de adesões${t}${ctx.consultor ? " do próprio consultor" : ""} com início de vigência no próprio mês do ranking.`,
      escopoProducao(ctx),
      "Se uma adesão do mês só for ativada no sistema no mês seguinte, ela continua contando para o mês da vigência.",
    ],
  };
}

function contaTicket() {
  return {
    titulo: "Ticket Médio",
    itens: [
      "Soma dos valores recebidos ÷ quantidade de mensalidades pagas no mês do ranking.",
      "Vale a data de pagamento: entram as mensalidades pagas entre o dia 1º e o último dia do mês do ranking.",
      "Conta toda a regional: as mensalidades da carteira dela e as dos consultores dela.",
    ],
    exemplo: "Em outubro, a regional recebeu 200 mensalidades que somam R$ 30.000,00. O ticket médio é R$ 30.000,00 ÷ 200 = R$ 150,00.",
  };
}

function contaInadimplencia() {
  return {
    titulo: "Inadimplência (%)",
    itens: [
      "Inadimplentes ÷ (Ativos + Inadimplentes) × 100, no mês do ranking.",
      "Quem tem o MENOR percentual fica em 1º.",
      'Ativos: contratos da regional e dos consultores dela com situação "Ativo".',
      'Inadimplentes: contratos com situação "Inadimplência-Cobrança" e "Inadimplência-Reativação".',
      "Vale a foto do fechamento do mês: o número do último dia do mês é o que conta.",
    ],
    exemplo: "900 ativos e 100 inadimplentes → 100 ÷ (900 + 100) = 10,0%.",
  };
}

function contaConsultoresAtivos() {
  return {
    titulo: "Consultores Ativos",
    itens: [
      "Quantidade de consultores diferentes da regional que fizeram pelo menos 1 adesão com início de vigência no mês do ranking.",
      "Cada consultor conta uma vez só, não importa quantas adesões fez.",
      "A própria regional não entra nessa conta, mesmo que tenha feito adesões.",
      "Se uma adesão do mês só for ativada no sistema no mês seguinte, ela continua contando para o mês da vigência.",
      "A lista de consultores leva em consideração o sistema do Teams Connect.",
    ],
    exemplo: "Em setembro, o João fez 5 adesões, a Maria fez 1 e a própria regional fez 3. Consultores Ativos = 2.",
  };
}

// ── Passo a passo no i4pro ───────────────────────────────────────────────

function passosAcesso(ctx) {
  const passos = [];
  if (ctx.consultor) {
    passos.push({ texto: "Somente a regional tem acesso ao i4pro: peça à sua regional para baixar o relatório." });
  }
  if (ctx.truck) {
    passos.push({ texto: "Entrar no i4pro da Truck." });
    passos.push({ texto: "Ir no menu Relatórios → Impressão.", img: IMG.menuTruck, alt: "Menu Relatórios > Impressão do i4pro da Truck, com os campos Grupo e Formulário" });
  } else {
    passos.push({ texto: "Entrar no i4pro da Brasil." });
    passos.push({ texto: "Ir no menu Relatórios → Auto - Impressão 2ª Via.", img: IMG.menuBrasil, alt: "Menu Relatórios > Auto - Impressão 2ª Via do i4pro da Brasil" });
  }
  return passos;
}

/** Passo do formulário. Na Truck o Grupo vem antes (Gerencial: Vendas, Metas, Relatório de Base). */
function passoFormulario(ctx, nome, img, alt) {
  if (ctx.truck) {
    return { texto: `Em Grupo, selecionar "Gerencial" e, em Formulário, selecionar "${nome}".` };
  }
  return { texto: `Em Formulário, selecionar "${nome}".`, img, alt };
}

function passoFiltroPessoa(ctx) {
  return ctx.consultor
    ? { texto: 'Filtrar a coluna "Consultor" só com o nome do consultor em questão.' }
    : { texto: 'Se você é regional "Gerente" e tem uma ou mais regionais abaixo da sua hierarquia, filtre a coluna "Regional" só com a sua própria regional.' };
}

function passosMetas(ctx) {
  return {
    passos: [
      ...passosAcesso(ctx),
      passoFormulario(ctx, "Metas", IMG.formularioMetas, 'Lista de formulários com "ID127000020 - Metas" selecionado'),
      {
        texto: "Selecionar o Ano e o Mês: o mês selecionado deve ser o mês SEGUINTE ao mês do ranking. Exemplo: para o ranking de outubro, baixe o relatório de Metas de novembro.",
        img: ctx.truck ? null : IMG.metasAnoMes,
        alt: "Formulário Metas com Ano Consulta 2026 e Mês Consulta Novembro",
      },
      { texto: "Baixar, salvar e abrir a planilha." },
      passoFiltroPessoa(ctx),
      { texto: 'Filtrar a coluna "Data Início Vigência" somente com o mês ANTERIOR ao mês do ranking. Exemplo: ranking do mês 10 → filtrar o mês 09.' },
      passoFiltroAteOntem("Data Contrato"),
    ],
    fechamento: "Com esses passos você terá o resultado exatamente igual ao do ranking.",
  };
}

function passosAdesoes(ctx, contarConsultores) {
  const formulario = ctx.truck ? "Vendas" : "Novos Contratos";
  const passos = [
    ...passosAcesso(ctx),
    passoFormulario(ctx, formulario, IMG.novosContratos, 'Formulário "ID127000009 - Novos Contratos" com Ano Consulta e Mês Consulta'),
    { texto: "Selecionar o Ano e o Mês: o mesmo mês do ranking." },
    { texto: "Baixar, salvar e abrir a planilha." },
    passoFiltroPessoa(ctx),
    { texto: 'Filtrar a coluna "Data Início Vigência" pelo mês do ranking.' },
    passoFiltroAteOntem("Data Contrato"),
  ];
  if (contarConsultores) {
    passos.push({ texto: 'Abrir o filtro da coluna "Consultor" e contar quantos nomes diferentes aparecem, sem contar o seu próprio nome.' });
  }
  return {
    passos,
    observacao: {
      ...OBS_ADESAO_MES_SEGUINTE,
      texto: contarConsultores
        ? OBS_ADESAO_MES_SEGUINTE.texto.replace("e some com o primeiro relatório.", "e junte com o primeiro relatório antes de contar os consultores.")
        : OBS_ADESAO_MES_SEGUINTE.texto,
    },
  };
}

function passosRecebimento(ctx) {
  return {
    passos: [
      ...passosAcesso(ctx),
      { texto: 'Em Formulário, selecionar "Recebimento".' },
      {
        texto: 'Selecionar o Ano e o Mês (o mesmo mês do ranking) e, em Referência, selecionar "Recebimento". A Referência já traz só os pagamentos do mês, então não é preciso filtrar a data de pagamento.',
        img: IMG.recebimento,
        alt: 'Formulário "ID127000010 - Recebimento" com Ano Consulta, Mês Consulta e Referência "Recebimento"',
      },
      { texto: "Baixar, salvar e abrir a planilha." },
      passoFiltroPessoa(ctx),
      passoFiltroAteOntem("Data Pagamento"),
      { texto: 'Somar a coluna "Valor Recebido" e dividir pela quantidade de linhas.' },
    ],
  };
}

function passosInadimplencia(ctx) {
  return {
    passos: [
      ...passosAcesso(ctx),
      {
        texto: 'Em Formulário, selecionar "Relatório de Base - Hierarquia".',
        img: IMG.baseHierarquia,
        alt: 'Formulário "ID139000003 - Relatório de Base - Hierarquia" com a lista de Situações',
      },
      { texto: 'Em Situação, selecionar "Ativo", baixar, abrir e contar as linhas. Esse é o número de Ativos.' },
      { texto: 'Em Situação, selecionar "Inadimplência-Cobrança", baixar e contar as linhas.' },
      { texto: 'Em Situação, selecionar "Inadimplência-Reativação", baixar e contar as linhas.' },
      { texto: "A soma dos dois passos anteriores é o número de Inadimplentes." },
      passoFiltroPessoa(ctx),
      { texto: "Calcular: Inadimplentes ÷ (Ativos + Inadimplentes) × 100." },
    ],
    aviso:
      "Este relatório não tem filtro de data: ele sempre mostra a base do momento em que é baixado e pode mudar a cada atualização do sistema. Por isso, vale o fechamento do último momento do mês: o último dia do mês, às 6h da manhã.",
  };
}

const CONFERE_BASE_100 = {
  titulo: "Para conferir se você tinha mais de 100 ativos",
  texto:
    'No último dia de cada mês, logo depois das 6h da manhã, baixe o "Relatório de Base - Hierarquia" com a situação "Ativo", conte as linhas e guarde a planilha. Ela é a foto da base usada no ranking do mês seguinte, porque o i4pro sempre mostra a base do momento em que o relatório é baixado.',
  img: IMG.baseHierarquia,
  alt: 'Formulário "ID139000003 - Relatório de Base - Hierarquia" com a Situação "Ativo"',
};

// ── Montagem das seções ──────────────────────────────────────────────────

/**
 * Regras por ranking: público, o que conta e o passo a passo. `ctx` = { truck, consultor }.
 * As chaves são os ids dos rankings (ranking-nacional.js / ranking-estadual.js).
 */
const REGRAS = {
  "nacional-brasil-ho-boletos": { publico: "homeoffice", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "nacional-brasil-consultores-boletos": { publico: "consultor", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "nacional-brasil-regional-adesao": { publico: "todas", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },
  "nacional-brasil-regional-boletos": { publico: "todas", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "nacional-brasil-regional-ticket": { publico: "top50Ticket", conta: contaTicket, passos: (c) => passosRecebimento(c) },
  "nacional-brasil-regional-inadimplencia": { publico: "top50Inadimplencia", conta: contaInadimplencia, passos: (c) => passosInadimplencia(c) },
  "nacional-brasil-regional-cons-ativos": { publico: "todas", conta: contaConsultoresAtivos, passos: (c) => passosAdesoes(c, true) },
  "nacional-truck-regional-adesao": { publico: "todas", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },
  "nacional-truck-regional-boletos": { publico: "todas", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "nacional-truck-consultor-adesao": { publico: "consultor", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },

  "estadual-brasil-regional-adesao": { publico: "todas", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },
  "estadual-brasil-regional-ticket": { publico: "base100", conta: contaTicket, passos: (c) => passosRecebimento(c), extra: CONFERE_BASE_100 },
  "estadual-brasil-consultores-adesao": { publico: "consultor", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false), soEstadual: true },
  "estadual-brasil-consultores-boletos": { publico: "consultor", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "estadual-truck-regional-adesao": { publico: "todas", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },
  "estadual-truck-regional-boletos": { publico: "todas", conta: contaBoletos, passos: (c) => passosMetas(c) },
  "estadual-truck-consultor-adesao": { publico: "consultor", conta: contaAdesoes, passos: (c) => passosAdesoes(c, false) },
  "estadual-truck-consultores-boletos": { publico: "consultor", conta: contaBoletos, passos: (c) => passosMetas(c), soEstadual: true },
};

function topN(ranking, estadual) {
  if (estadual) return ranking.grupo === "truck" ? 5 : 20;
  return ranking.grupo === "truck" ? 10 : 20;
}

function secao(ranking, estadual) {
  const regra = REGRAS[ranking.id];
  const ctx = { truck: ranking.grupo === "truck", consultor: ranking.entidade === "consultor" };
  const n = topN(ranking, estadual);
  const publico = PUBLICO[regra.publico](ctx).slice();
  publico.push(
    ctx.consultor
      ? `A tela mostra os ${n} primeiros, com o nome do consultor e a regional dele.`
      : `A tela mostra as ${n} primeiras.`
  );

  return {
    id: ranking.id,
    path: ranking.path,
    menu: estadual ? "Ranking Estadual" : "Ranking Nacional",
    estadual,
    empresa: ctx.truck ? "Truck" : "Brasil",
    titulo: ranking.titulo,
    entidade: ranking.entidade,
    soEstadual: !!regra.soEstadual,
    blocoEstadual: estadual ? BLOCO_ESTADUAL : "",
    publico,
    conta: regra.conta(ctx),
    desempate: desempate(ranking.metrica, ctx),
    notaDesempate: notaDesempate(ctx),
    conferencia: { sistema: ctx.truck ? "i4pro da Truck" : "i4pro da Brasil", atualizacao: AVISO_ATUALIZACAO, ...regra.passos(ctx) },
    extra: regra.extra || null,
  };
}

/**
 * Grupos da página, na ordem do menu: Nacional Brasil/Truck, depois Estadual Brasil/Truck.
 * Com o Ranking Estadual desligado (RANKING_ESTADUAL_ATIVO), só os do Nacional.
 */
export const GRUPOS_REGULAMENTO = [
  { key: "nacional-brasil", label: "Ranking Nacional · Brasil", estadual: false, secoes: RANKINGS_NACIONAIS.filter((r) => r.grupo === "brasil").map((r) => secao(r, false)) },
  { key: "nacional-truck", label: "Ranking Nacional · Truck", estadual: false, secoes: RANKINGS_NACIONAIS.filter((r) => r.grupo === "truck").map((r) => secao(r, false)) },
  { key: "estadual-brasil", label: "Ranking Estadual · Brasil", estadual: true, secoes: RANKINGS_ESTADUAIS.filter((r) => r.grupo === "brasil").map((r) => secao(r, true)) },
  { key: "estadual-truck", label: "Ranking Estadual · Truck", estadual: true, secoes: RANKINGS_ESTADUAIS.filter((r) => r.grupo === "truck").map((r) => secao(r, true)) },
].filter((g) => !g.estadual || RANKING_ESTADUAL_ATIVO);

export const REGULAMENTO_PATH = "/biteams/rk-regulamento";

/** Rota do regulamento já na seção do ranking (botão do final de nacional.vue). */
export function linkRegulamento(rankingId) {
  return { path: REGULAMENTO_PATH, hash: `#${rankingId}` };
}
